import { getNostoApi } from '@theme/bt-nosto-search';
import { productHandle } from '@theme/bt-nosto-placement';

const LOOK_ROW_SECTION = 'bt-pdp-look-product';
const LOOK_MAX = 12;
const NOSTO_HTML_TIMEOUT_MS = 10000;

/**
 * PDP get this look — the on-page row is Nosto HTML only.
 * Handles come from `[data-look-product]`; Shopify section rows
 * are prefetched for the drawer.
 *
 * @extends {HTMLElement}
 */
class BtPdpLook extends HTMLElement {
  /** @type {Promise<void> | null} */
  #rowsReady = null;

  #drawerReady = false;

  /** @type {MutationObserver | null} */
  #slotObserver = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick, true);
    this.#boot();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick, true);
    this.#slotObserver?.disconnect();
    this.#slotObserver = null;
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const trigger = target.closest('[data-shop-the-look-open]');
    if (!(trigger instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const open = async () => {
      const lookId = trigger.dataset.lookId || this.dataset.lookId || '';
      if (!lookId) return;
      await customElements.whenDefined('shop-the-look-drawer');
      const drawer = this.querySelector('shop-the-look-drawer');
      if (drawer && typeof drawer.openLook === 'function') {
        drawer.openLook(lookId, trigger.isConnected ? trigger : this.#lookButton() || undefined);
      }
    };

    if (this.#drawerReady) {
      open();
    } else if (this.#rowsReady) {
      this.#rowsReady.then(open);
    }
  };

  async #boot() {
    const placementId = this.dataset.nostoPlacement?.trim();
    const slot = this.#nostoSlot();
    if (!placementId || !slot) {
      this.#onEmpty();
      return;
    }

    try {
      await waitForNostoHtml(slot);
      if (!slotHasLookHtml(slot)) {
        this.#onEmpty();
        return;
      }

      this.#bindInjectedButton(slot);
      this.#watchSlot(slot);
      this.hidden = false;
      this.#rowsReady = this.#prefetchDrawer(slot);
    } catch (error) {
      console.warn('[bt-pdp-look] Nosto HTML placement failed', error);
      this.#onEmpty();
    }
  }

  /**
   * @returns {HTMLElement | null}
   */
  #nostoSlot() {
    const slot = this.querySelector('[data-look-nosto]');
    return slot instanceof HTMLElement ? slot : null;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #lookButton() {
    const button = this.querySelector('[data-shop-the-look-open]');
    return button instanceof HTMLElement ? button : null;
  }

  /**
   * Nosto’s template is the visible callout. Point its button at our drawer.
   * @param {HTMLElement} slot
   */
  #bindInjectedButton(slot) {
    const button =
      slot.querySelector('[data-shop-the-look-open]') || slot.querySelector('button, a, [role="button"]');
    if (!(button instanceof HTMLElement)) return;

    button.setAttribute('data-shop-the-look-open', '');
    button.dataset.lookId = this.dataset.lookId || '';
    if (button instanceof HTMLButtonElement) button.disabled = false;
  }

  /**
   * Re-stamp the trigger when Nosto re-renders the placement.
   * @param {HTMLElement} slot
   */
  #watchSlot(slot) {
    this.#slotObserver?.disconnect();
    this.#slotObserver = new MutationObserver(() => {
      if (slot.querySelector('[data-shop-the-look-open]')) return;
      this.#bindInjectedButton(slot);
    });
    this.#slotObserver.observe(slot, { childList: true, subtree: true });
  }

  /**
   * @returns {HTMLElement | null}
   */
  #drawerList() {
    const drawer = this.querySelector('shop-the-look-drawer');
    const list = drawer?.querySelector('[data-stl-products]');
    return list instanceof HTMLElement ? list : null;
  }

  /**
   * Drawer handles from the injected campaign HTML.
   * @param {HTMLElement} slot
   * @returns {NostoLookProduct[]}
   */
  #productsFromHtml(slot) {
    return this.#filterProducts(parseNostoLookProducts(slot));
  }

  /**
   * @param {NostoLookProduct[]} products
   * @returns {NostoLookProduct[]}
   */
  #filterProducts(products) {
    const currentHandle = (this.dataset.productHandle || '').toLowerCase();
    const currentId = String(this.dataset.productId || '');

    return products
      .filter((product) => {
        const handle = productHandle(product).toLowerCase();
        const id = String(product.product_id || product.productId || '');
        if (currentId && id && id === currentId) return false;
        if (handle && handle === currentHandle) return false;
        return Boolean(handle);
      })
      .slice(0, LOOK_MAX);
  }

  /**
   * @param {HTMLElement} slot
   */
  async #prefetchDrawer(slot) {
    try {
      const products = this.#productsFromHtml(slot);
      if (!products.length) {
        console.warn('[bt-pdp-look] Drawer has no products');
        return;
      }

      await this.#hydrateDrawer(products);
    } catch (error) {
      console.warn('[bt-pdp-look] Drawer rows failed to load', error);
    } finally {
      this.#drawerReady = true;
    }
  }

  /**
   * @param {NostoLookProduct[]} products
   */
  async #hydrateDrawer(products) {
    const loaded = await this.#fetchRows(products);
    const list = this.#drawerList();
    if (!list || !loaded.length) {
      console.warn('[bt-pdp-look] Drawer rows failed to load');
      return;
    }

    loaded.forEach((entry, index) => {
      entry.row.dataset.productIndex = String(index + 1);
    });
    list.replaceChildren(...loaded.map((entry) => entry.row));
    const panel = list.closest('[data-stl-look-panel]');
    const drawer = this.querySelector('shop-the-look-drawer');
    if (panel instanceof HTMLElement && drawer && typeof drawer.syncPanelMeta === 'function') {
      drawer.syncPanelMeta(panel);
    }
  }

  /**
   * @param {NostoLookProduct[]} products
   * @returns {Promise<Array<{ product: NostoLookProduct, row: HTMLElement }>>}
   */
  async #fetchRows(products) {
    const results = await Promise.all(
      products.map(async (product, index) => {
        const handle = productHandle(product);
        if (!handle) return null;
        try {
          const url = lookRowUrl(handle);
          const response = await fetch(url);
          if (!response.ok) return null;
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const row = doc.querySelector('[data-stl-product]');
          if (!(row instanceof HTMLElement)) return null;
          const imported = document.importNode(row, true);
          imported.dataset.productIndex = String(index + 1);
          return { product, row: imported };
        } catch {
          return null;
        }
      })
    );

    return results.filter((entry) => entry && entry.row instanceof HTMLElement);
  }

  #onEmpty() {
    this.#drawerList()?.replaceChildren();
    this.hidden = !this.hasAttribute('data-design-mode');
  }
}

/**
 * @typedef {object} NostoLookProduct
 * @property {string} [handle]
 * @property {string} [url]
 * @property {string} [name]
 * @property {string} [title]
 * @property {string} [image]
 * @property {string} [image_url]
 * @property {string} [imageUrl]
 * @property {string} [product_id]
 * @property {string} [productId]
 */

/**
 * Product nodes from the campaign HTML.
 * @param {HTMLElement} slot
 * @returns {NostoLookProduct[]}
 */
function parseNostoLookProducts(slot) {
  const tagged = [...slot.querySelectorAll('[data-look-product]')];
  if (tagged.length) {
    return tagged.map((el) => ({
      handle: el.dataset.handle || '',
      url: el.dataset.url || '',
      name: el.dataset.name || '',
      image_url: el.dataset.image || '',
      product_id: el.dataset.productId || '',
    }));
  }

  const liveItems = [...slot.querySelectorAll('.prd-CompleteTheLook_Item, [x-nosto-handle]')];
  if (liveItems.length) {
    return liveItems.map((el) => {
      const image = el.querySelector('img');
      return {
        handle: el.getAttribute('x-nosto-handle') || '',
        url: el instanceof HTMLElement ? el.dataset.productUrl || '' : '',
        name: el.querySelector('.prd-Card_SubTitle')?.textContent?.trim() || image?.alt || '',
        image_url: image?.currentSrc || image?.src || '',
        product_id: el.querySelector('[data-productid], [data-productId]')?.getAttribute('data-productid') || '',
      };
    });
  }

  const seen = new Set();
  /** @type {NostoLookProduct[]} */
  const fromLinks = [];
  slot.querySelectorAll('a[href*="/products/"]').forEach((link) => {
    if (!(link instanceof HTMLAnchorElement)) return;
    const url = link.getAttribute('href') || '';
    const handle = productHandle({ url });
    if (!handle || seen.has(handle)) return;
    seen.add(handle);
    const image = link.querySelector('img') || link.parentElement?.querySelector('img');
    fromLinks.push({
      handle,
      url,
      name: image?.alt || link.textContent?.trim() || '',
      image_url: image?.currentSrc || image?.src || '',
    });
  });
  return fromLinks;
}

/**
 * Campaign HTML landed (visible callout). Products are optional here.
 * @param {HTMLElement} slot
 */
function slotHasLookHtml(slot) {
  if (slot.querySelector('[data-look-source], .bt-pdp-stack__look-row, [data-look-product], .prd-CompleteTheLook_Item')) {
    return true;
  }
  return slot.childElementCount > 0 && Boolean(slot.textContent?.trim());
}

/**
 * @param {HTMLElement} slot
 * @param {number} [timeoutMs]
 */
async function waitForNostoHtml(slot, timeoutMs = NOSTO_HTML_TIMEOUT_MS) {
  if (slotHasLookHtml(slot)) return;

  const api = await getNostoApi(undefined, { requireSearch: false });
  if (!api) return;

  await new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      window.clearTimeout(timer);
      resolve();
    };

    const observer = new MutationObserver(() => {
      if (slotHasLookHtml(slot)) finish();
    });
    observer.observe(slot, { childList: true, subtree: true });

    if (typeof api.listen === 'function') {
      api.listen('postrender', () => {
        if (slotHasLookHtml(slot)) finish();
      });
    }

    if (typeof api.load === 'function') {
      Promise.resolve(api.load())
        .catch(() => {})
        .then(() => {
          if (slotHasLookHtml(slot)) finish();
        });
    }

    const timer = window.setTimeout(finish, timeoutMs);
  });
}

/**
 * @param {string} handle
 */
function lookRowUrl(handle) {
  const root = window.Shopify?.routes?.root || '/';
  const base = root.endsWith('/') ? root : `${root}/`;
  return `${base}products/${encodeURIComponent(handle)}?section_id=${LOOK_ROW_SECTION}`;
}

if (!customElements.get('bt-pdp-look')) {
  customElements.define('bt-pdp-look', BtPdpLook);
}
